if (!customElements.get('collection-filters')) {
  class CollectionFilters extends HTMLElement {
    constructor() {
      super();
      this.drawer = null;
      this.filterButton = null;
      this.gridViewButtons = [];
      this.abortController = null;
      this._drawerOpenUnsub = null;
      this._drawerCloseUnsub = null;
      this.desktopMediaQuery = window.matchMedia('(min-width: 1024px)');
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.toolbarResizeObserver = null;
      this.handleDocumentChange = null;
      this.handleDocumentClick = null;
    }

    connectedCallback() {
      this.filterButton = this.querySelector('[data-filter-button]');
      this.drawer = this.filterButton
        ? document.getElementById(this.filterButton.getAttribute('aria-controls'))
        : null;
      this.gridViewButtons = [...this.querySelectorAll('[data-grid-view]')];

      this.initFilterDrawer();
      this.initGridViewSelector();
      this.initDelegatedEvents();
      this.initSidebarMode();
      this.initToolbarHeightSync();

      this.onPopState = () => this.fetchAndUpdatePage(window.location.href, 'filter', { fromPopState: true });
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.removeEventListener('popstate', this.onPopState);
      this._drawerOpenUnsub?.();
      this._drawerCloseUnsub?.();
      this.desktopMediaQuery.removeEventListener('change', this.handleViewportChange);
      this.toolbarResizeObserver?.disconnect();
      this._headerOffsetUnsub?.();
      if (this.handleDocumentChange) document.removeEventListener('change', this.handleDocumentChange);
      if (this.handleDocumentClick) document.removeEventListener('click', this.handleDocumentClick);
      if (this._handleToolbarWindowResize) window.removeEventListener('resize', this._handleToolbarWindowResize);
    }

    initToolbarHeightSync() {
      const toolbar = this.querySelector('.collection-filters__bar');
      if (!toolbar || typeof ResizeObserver === 'undefined') return;

      let groupBottom = document.querySelector('theme-sticky-header')?.offset ?? 0;

      const updateHeight = () => {
        const toolbarHeight = toolbar.getBoundingClientRect().height;
        document.documentElement.style.setProperty(
          '--collection-filters-panel-top',
          `${groupBottom + toolbarHeight}px`
        );
      };

      this._headerOffsetUnsub = theme.pubsub.subscribe(theme.pubsub.events.HEADER_OFFSET_CHANGE, ({ offset }) => {
        groupBottom = offset;
        updateHeight();
      });
      this.toolbarResizeObserver = new ResizeObserver(updateHeight);
      this.toolbarResizeObserver.observe(toolbar);
      window.addEventListener('resize', updateHeight);
      this._handleToolbarWindowResize = updateHeight;
      updateHeight();
    }

    initDelegatedEvents() {
      this.handleDocumentChange = (e) => {
        if (e.target.matches('[data-filter-input]')) {
          this.handleFilterChange(e);
        } else if (e.target.matches('[data-sort-input]')) {
          this.handleSortChange(e);
        }
      };

      this.handleDocumentClick = (e) => {
        const chipRemove = e.target.closest('.applied-filters__chip-remove');
        if (chipRemove) {
          e.preventDefault();
          this.fetchAndUpdatePage(chipRemove.href);
          return;
        }

        const clearAll = e.target.closest('.applied-filters__reset');
        if (clearAll) {
          e.preventDefault();
          this.fetchAndUpdatePage(clearAll.href);
          return;
        }

        const applyBtn = e.target.closest('[data-filter-apply]');
        if (applyBtn) {
          this.closePanel();
          return;
        }
      };

      document.addEventListener('change', this.handleDocumentChange);
      document.addEventListener('click', this.handleDocumentClick);
    }

    initSidebarMode() {
      if (!this.drawer || !this.drawer.hasAttribute('data-filter-drawer')) return;

      this.desktopMediaQuery.addEventListener('change', this.handleViewportChange);
      this.handleViewportChange(this.desktopMediaQuery);
    }

    isDesktopLayout() {
      return Boolean(this.drawer?.hasAttribute('data-filter-drawer')) && this.desktopMediaQuery.matches;
    }

    handleViewportChange(e) {
      if (!this.drawer) return;
      const panel = this.drawer.querySelector('[data-drawer-panel]');
      const isDesktop = e.matches;

      if (isDesktop) {
        this.drawer.removeAttribute('data-open');
        panel?.removeAttribute('role');
        panel?.removeAttribute('aria-modal');
      } else {
        this.drawer.removeAttribute('data-panel-open');
        panel?.setAttribute('role', 'dialog');
        panel?.setAttribute('aria-modal', 'true');
      }

      this.syncFilterToggleState();
    }

    togglePanel() {
      if (!this.drawer) return;

      if (this.isDesktopLayout()) {
        this.drawer.hasAttribute('data-panel-open')
          ? this.drawer.removeAttribute('data-panel-open')
          : this.drawer.setAttribute('data-panel-open', '');
        this.syncFilterToggleState();
        return;
      }

      this.drawer.toggle();
    }

    closePanel() {
      if (!this.drawer) return;

      if (this.isDesktopLayout()) {
        this.drawer.removeAttribute('data-panel-open');
        this.syncFilterToggleState();
        return;
      }

      this.drawer.close();
    }

    initFilterDrawer() {
      if (!this.filterButton || !this.drawer) return;

      this.filterButton.addEventListener('click', () => this.togglePanel());
      this.drawer.querySelector('[data-filter-drawer-close]')?.addEventListener('click', () => this.closePanel());

      if (window.theme?.pubsub) {
        const events = theme.pubsub.events;
        const drawerId = this.drawer.id;
        this._drawerOpenUnsub = theme.pubsub.subscribe(events.DRAWER_OPEN, ({ id } = {}) => {
          if (id !== drawerId) return;
          this.syncFilterToggleState();
        });
        this._drawerCloseUnsub = theme.pubsub.subscribe(events.DRAWER_CLOSE, ({ id } = {}) => {
          if (id !== drawerId) return;
          this.syncFilterToggleState();
        });
      }

      this.syncFilterToggleState();
    }

    isFilterDrawerOpen() {
      if (!this.drawer) return false;
      return this.drawer.hasAttribute('data-open') || this.drawer.hasAttribute('data-panel-open');
    }

    syncFilterToggleState() {
      if (!this.filterButton) return;

      const isOpen = this.isFilterDrawerOpen();
      this.filterButton.setAttribute('aria-expanded', String(isOpen));

      const toggleLabel = this.filterButton.querySelector('[data-filter-toggle-label]');
      if (!toggleLabel) return;

      const isDesktop = this.desktopMediaQuery.matches;
      if (isDesktop && toggleLabel.dataset.labelShow && toggleLabel.dataset.labelHide) {
        toggleLabel.textContent = isOpen ? toggleLabel.dataset.labelHide : toggleLabel.dataset.labelShow;
      } else if (toggleLabel.dataset.labelMobile) {
        toggleLabel.textContent = toggleLabel.dataset.labelMobile;
      }
    }

    async handleFilterChange(e) {
      const input = e.target;
      const isChecked = input.checked;
      let targetUrl;

      if (isChecked) {
        targetUrl = input.dataset.filterUrl;
      } else if (input.dataset.filterType === 'price_range') {
        const url = new URL(window.location.href);
        url.searchParams.delete(input.dataset.filterParamGte);
        url.searchParams.delete(input.dataset.filterParamLte);
        targetUrl = url.toString();
      } else {
        targetUrl = input.dataset.filterRemoveUrl;
      }

      if (!targetUrl) return;

      await this.fetchAndUpdatePage(targetUrl);
    }

    async handleSortChange(e) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      await this.fetchAndUpdatePage(url.toString(), 'sort');
    }

    async fetchAndUpdatePage(targetUrl, eventType = 'filter', { fromPopState = false } = {}) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      const drawerWasOpen = this.isFilterDrawerOpen();
      this.setLoading(true);

      try {
        const cacheKey = theme.cache.scopedKey(`cfilters:${targetUrl}`);
        let html = theme.cache.get(cacheKey);
        if (html === null) {
          const response = await fetch(targetUrl, { signal });
          html = await response.text();
          if (response.ok) theme.cache.set(cacheKey, html, theme.cache.TTL_FRESH);
        }
        const doc = new DOMParser().parseFromString(html, 'text/html');

        this.updateDrawerContent(doc);
        this.updateProductGrid(doc);
        this.updatePagination(doc);
        this.updateApplyButtonCount(doc);
        this.updateResultsCount(doc);

        if (!fromPopState) window.history.pushState({}, '', targetUrl);

        if (drawerWasOpen) {
          this.drawer?.refreshFocus();
        }

        if (!fromPopState) this.publishFilterEvent(eventType, doc, targetUrl);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Filter/sort update error:', error);
        window.location.href = targetUrl;
      } finally {
        if (!signal.aborted) {
          this.abortController = null;
          this.setLoading(false);
        }
      }
    }

    updateDrawerContent(doc) {
      const newContent = doc.querySelector('[data-filter-drawer-content]');
      const currentContent = this.drawer?.querySelector('[data-filter-drawer-content]');
      if (newContent && currentContent) {
        currentContent.innerHTML = newContent.innerHTML;
      }
    }

    getGridSelector() {
      return this.dataset.gridTarget || '#product-grid';
    }

    getPaginationSelector() {
      return this.dataset.paginationTarget || '.collection-grid__pagination';
    }

    updateProductGrid(doc) {
      const gridSelector = this.getGridSelector();
      const newGrid = doc.querySelector(gridSelector);
      const currentGrid = document.querySelector(gridSelector);
      if (!newGrid || !currentGrid) return;

      currentGrid.innerHTML = newGrid.innerHTML;

      const viewSelector = this.querySelector('[data-grid-view-selector]');
      if (!viewSelector) return;

      const savedView = localStorage.getItem('collection-grid-view');
      if (savedView) {
        this.applyGridView(savedView);
      }
    }

    updatePagination(doc) {
      const paginationSelector = this.getPaginationSelector();
      const newPagination = doc.querySelector(paginationSelector);
      const currentPagination = document.querySelector(paginationSelector);
      if (newPagination && currentPagination) {
        currentPagination.innerHTML = newPagination.innerHTML;
      }
    }

    updateApplyButtonCount(doc) {
      const newCount = doc.querySelector('[data-filter-apply-count]');
      const countEl = this.drawer?.querySelector('[data-filter-apply-count]');
      if (!newCount || !countEl) return;

      countEl.textContent = newCount.textContent.trim();
    }

    updateResultsCount(doc) {
      const newCount = doc.querySelector('[data-results-count]');
      const currentCount = document.querySelector('[data-results-count]');
      if (newCount && currentCount) currentCount.innerHTML = newCount.innerHTML;

      const newProductCount = doc.querySelector('[data-product-count]');
      const currentProductCount = document.querySelector('[data-product-count]');
      if (newProductCount && currentProductCount) currentProductCount.textContent = newProductCount.textContent.trim();
    }

    publishFilterEvent(eventType, doc, url) {
      if (!window.theme?.pubsub) return;
      const events = theme.pubsub.events;

      if (eventType === 'sort') {
        theme.pubsub.publish(events.SORT_CHANGED, { url });
        return;
      }

      const activeCount = doc.querySelectorAll('[data-active-filters] .applied-filters__chip').length;
      if (activeCount > 0) {
        theme.pubsub.publish(events.FILTER_APPLIED, { url, activeCount });
      } else {
        theme.pubsub.publish(events.FILTER_CLEARED, { url });
      }
    }

    setLoading(show) {
      const drawerContent = this.drawer?.querySelector('[data-filter-drawer-content]');
      const grid = document.querySelector(this.getGridSelector());

      if (show) {
        if (drawerContent) {
          drawerContent.style.opacity = '0.5';
          drawerContent.style.pointerEvents = 'none';
        }

        if (grid) {
          grid.style.opacity = '0.5';
          grid.style.pointerEvents = 'none';
          grid.style.transition = 'opacity 0.2s ease';
        }

        let spinner = document.querySelector('[data-filter-spinner]');
        if (!spinner && grid) {
          spinner = document.createElement('div');
          spinner.setAttribute('data-filter-spinner', '');
          spinner.className = 'collection-filters__spinner-overlay';
          spinner.innerHTML = '<div class="collection-filters__spinner"></div>';
          grid.parentElement.style.position = 'relative';
          grid.parentElement.appendChild(spinner);
        }
      } else {
        if (drawerContent) {
          drawerContent.style.opacity = '';
          drawerContent.style.pointerEvents = '';
        }

        if (grid) {
          grid.style.opacity = '';
          grid.style.pointerEvents = '';
          grid.style.transition = '';
        }

        const spinner = document.querySelector('[data-filter-spinner]');
        if (spinner) spinner.remove();
      }
    }

    initGridViewSelector() {
      if (this.gridViewButtons.length === 0) return;

      const defaultView = theme.utils.defaultGridView(this.dataset.defaultGridView, this.dataset.defaultGridViewMobile);
      const storedView = localStorage.getItem('collection-grid-view');
      const savedView = this.normalizeGridView(storedView, defaultView);
      if (storedView && storedView !== savedView) {
        localStorage.setItem('collection-grid-view', savedView);
      }

      this.setActiveGridView(savedView);
      this.applyGridView(savedView);

      this.gridViewButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const view = button.dataset.gridView;
          this.setActiveGridView(view);
          this.applyGridView(view);
          localStorage.setItem('collection-grid-view', view);
        });
      });
    }

    normalizeGridView(rawView, fallbackView) {
      if (rawView === '2' || rawView === '4' || rawView === '5') return rawView;
      if (rawView === '12') return '5';
      return fallbackView;
    }

    setActiveGridView(view) {
      this.gridViewButtons.forEach((button) => {
        button.classList.toggle('collection-grid-view__button--active', button.dataset.gridView === view);
      });
    }

    applyGridView(view) {
      if (window.theme?.pubsub) {
        theme.pubsub.publish(theme.pubsub.events.GRID_VIEW_CHANGE, { view });
      }

      document.dispatchEvent(new CustomEvent('collection-grid-view:change', {
        detail: { view }
      }));
    }
  }

  customElements.define('collection-filters', CollectionFilters);
}

if (!customElements.get('price-range-slider')) {
  class PriceRangeSlider extends HTMLElement {
    constructor() {
      super();
      this.minInput = this.querySelector('.price-range-slider__input--min');
      this.maxInput = this.querySelector('.price-range-slider__input--max');
      this.minLabel = this.querySelector('.price-range-slider__label--min');
      this.maxLabel = this.querySelector('.price-range-slider__label--max');
      this.trackFill = this.querySelector('.price-range-slider__track-fill');

      this.debounceTimer = null;
      this.handleInputChange = this.handleInputChange.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleThumbFocus = this.handleThumbFocus.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
    }

    connectedCallback() {
      this.min = parseInt(this.dataset.min) || 0;
      this.max = parseInt(this.dataset.max) || 100;
      this.currentMin = this.parseIntOrDefault(this.dataset.currentMin, this.min);
      this.currentMax = this.parseIntOrDefault(this.dataset.currentMax, this.max);
      this.paramName = this.dataset.paramName || 'filter.p.price';
      this.currency = this.dataset.currency || '{{ amount }}';
      this.trackContainer = this.querySelector('.price-range-slider__track-container');

      this.updateTrackFill();
      this.setActiveThumb(this.currentMin >= this.currentMax ? this.maxInput : this.minInput);

      this.minInput?.addEventListener('input', this.handleInputChange);
      this.maxInput?.addEventListener('input', this.handleInputChange);
      this.minInput?.addEventListener('change', this.handleChange);
      this.maxInput?.addEventListener('change', this.handleChange);
      this.minInput?.addEventListener('focus', this.handleThumbFocus);
      this.maxInput?.addEventListener('focus', this.handleThumbFocus);
      this.trackContainer?.addEventListener('pointermove', this.handlePointerMove);
    }

    disconnectedCallback() {
      this.minInput?.removeEventListener('input', this.handleInputChange);
      this.maxInput?.removeEventListener('input', this.handleInputChange);
      this.minInput?.removeEventListener('change', this.handleChange);
      this.maxInput?.removeEventListener('change', this.handleChange);
      this.minInput?.removeEventListener('focus', this.handleThumbFocus);
      this.maxInput?.removeEventListener('focus', this.handleThumbFocus);
      this.trackContainer?.removeEventListener('pointermove', this.handlePointerMove);

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
    }

    parseIntOrDefault(value, defaultValue) {
      const parsed = parseInt(value);
      return Number.isNaN(parsed) ? defaultValue : parsed;
    }

    setActiveThumb(input) {
      this.minInput?.classList.toggle('price-range-slider__input--front', input === this.minInput);
      this.maxInput?.classList.toggle('price-range-slider__input--front', input === this.maxInput);
    }

    handleThumbFocus(event) {
      this.setActiveThumb(event.target);
    }

    handlePointerMove(event) {
      if (!this.trackContainer) return;

      const range = this.max - this.min;
      if (range === 0) return;

      const rect = this.trackContainer.getBoundingClientRect();
      if (rect.width === 0) return;

      const pointerPercent = ((event.clientX - rect.left) / rect.width) * 100;
      const minPercent = ((this.currentMin - this.min) / range) * 100;
      const maxPercent = ((this.currentMax - this.min) / range) * 100;
      const distanceToMin = Math.abs(pointerPercent - minPercent);
      const distanceToMax = Math.abs(pointerPercent - maxPercent);

      let nearestInput;
      if (distanceToMin === distanceToMax) {
        const midpoint = (minPercent + maxPercent) / 2;
        nearestInput = pointerPercent >= midpoint ? this.maxInput : this.minInput;
      } else {
        nearestInput = distanceToMin < distanceToMax ? this.minInput : this.maxInput;
      }

      this.setActiveThumb(nearestInput);
    }

    handleInputChange() {
      let minValue = parseInt(this.minInput.value);
      let maxValue = parseInt(this.maxInput.value);

      if (minValue > maxValue) {
        minValue = maxValue;
        this.minInput.value = minValue;
      }

      if (maxValue < minValue) {
        maxValue = minValue;
        this.maxInput.value = maxValue;
      }

      this.currentMin = minValue;
      this.currentMax = maxValue;

      this.updateLabels();
      this.updateTrackFill();
    }

    handleChange() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(() => {
        this.applyFilter();
      }, 300);
    }

    applyFilter() {
      const url = new URL(window.location.href);
      const minValue = parseInt(this.minInput.value);
      const maxValue = parseInt(this.maxInput.value);

      if (minValue === this.min && maxValue === this.max) {
        url.searchParams.delete(`${this.paramName}.gte`);
        url.searchParams.delete(`${this.paramName}.lte`);
      } else {
        if (minValue > this.min) {
          url.searchParams.set(`${this.paramName}.gte`, (minValue / 100).toFixed(2));
        } else {
          url.searchParams.delete(`${this.paramName}.gte`);
        }

        if (maxValue < this.max) {
          url.searchParams.set(`${this.paramName}.lte`, (maxValue / 100).toFixed(2));
        } else {
          url.searchParams.delete(`${this.paramName}.lte`);
        }
      }

      const collectionFilters = this.closest('collection-filters') || document.querySelector('collection-filters');
      if (collectionFilters) {
        collectionFilters.fetchAndUpdatePage(url.toString());
      }
    }

    updateLabels() {
      if (this.minLabel) {
        this.minLabel.textContent = this.formatMoney(this.currentMin);
      }
      if (this.maxLabel) {
        this.maxLabel.textContent = this.formatMoney(this.currentMax);
      }
    }

    updateTrackFill() {
      if (!this.trackFill) return;

      const range = this.max - this.min;
      if (range === 0) return;
      const minPercent = ((this.currentMin - this.min) / range) * 100;
      const maxPercent = ((this.currentMax - this.min) / range) * 100;

      this.trackFill.style.left = `${minPercent}%`;
      this.trackFill.style.width = `${maxPercent - minPercent}%`;
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.floor(cents / 100);

      const amountWithComma = amount.replace('.', ',').replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
      const amountNoDecimalsWithComma = amountNoDecimals.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');

      const amountWithApostrophe = amount.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1'");

      return this.currency
        .replace('{{amount_with_comma_separator}}', amountWithComma)
        .replace('{{ amount_with_comma_separator }}', amountWithComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
        .replace('{{ amount_no_decimals_with_comma_separator }}', amountNoDecimalsWithComma)
        .replace('{{amount_with_apostrophe_separator}}', amountWithApostrophe)
        .replace('{{ amount_with_apostrophe_separator }}', amountWithApostrophe)
        .replace('{{amount}}', amount)
        .replace('{{ amount }}', amount)
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{ amount_no_decimals }}', amountNoDecimals);
    }
  }

  customElements.define('price-range-slider', PriceRangeSlider);
}
